import { memo, useMemo } from 'react';
import {
  Accordion,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';

export type MappedQuizAnswer = {
  question_id?: number;
  question: string;
  given_answer?: string | null;
  given_answer_numeric?: number | string;
  correct_answer?: number | string;
  correct_answer_text?: string | null;
  is_correct?: boolean;
};

export type QuizAttempt = {
  attempt: number;
  field?: string;
  answers: MappedQuizAnswer[];
};

type CourseQuizAnswersProps = {
  quizAttempts?: QuizAttempt[] | null;
  /** Flat list from API (all attempts concatenated in answer_1…answer_5 order). */
  mappedQuestionsAnswers?: MappedQuizAnswer[] | null;
  answer_1?: unknown;
  answer_2?: unknown;
  answer_3?: unknown;
  answer_4?: unknown;
  answer_5?: unknown;
};

const ANSWER_FIELDS = [
  'answer_1',
  'answer_2',
  'answer_3',
  'answer_4',
  'answer_5',
] as const;

function parseAnswerField(value: unknown): Record<string, unknown> | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function withCorrectness(item: MappedQuizAnswer): MappedQuizAnswer {
  if (item.is_correct != null) return item;
  if (item.given_answer_numeric == null || item.correct_answer == null) {
    return item;
  }
  return {
    ...item,
    is_correct:
      Number(item.given_answer_numeric) === Number(item.correct_answer),
  };
}

function looksEnriched(item: MappedQuizAnswer | undefined): boolean {
  if (!item) return false;
  return (
    Boolean(item.question) &&
    !/^question-\d+$/i.test(item.question) &&
    (item.given_answer != null || item.correct_answer != null)
  );
}

/**
 * Prefer API enrichment (question text + correctness). Raw answer_* fields are
 * only `{ "question-<id>": <optionIndex> }` and are not useful alone.
 */
function resolveAttempts(props: CourseQuizAnswersProps): QuizAttempt[] {
  if (props.quizAttempts && props.quizAttempts.length > 0) {
    const enriched = props.quizAttempts.map(attempt => ({
      ...attempt,
      answers: attempt.answers.map(withCorrectness),
    }));
    if (enriched.some(a => a.answers.some(looksEnriched))) {
      return enriched;
    }
  }

  const mapped = (props.mappedQuestionsAnswers ?? []).map(withCorrectness);
  const fieldEntries = ANSWER_FIELDS.map((field, index) => ({
    field,
    attempt: index + 1,
    parsed: parseAnswerField(props[field]),
  })).filter(entry => entry.parsed);

  if (mapped.length > 0 && fieldEntries.length > 0) {
    let cursor = 0;
    return fieldEntries.map(entry => {
      const keyCount = Object.keys(entry.parsed!).length;
      const slice = mapped.slice(cursor, cursor + keyCount);
      cursor += keyCount;

      if (slice.length === keyCount && slice.every(looksEnriched)) {
        return {
          attempt: entry.attempt,
          field: entry.field,
          answers: slice,
        };
      }

      // Match by question id when slice boundaries are off
      const answers = Object.entries(entry.parsed!).map(([key, value]) => {
        const questionId = Number.parseInt(key.split('-')[1] ?? '', 10);
        const match =
          mapped.find(
            item =>
              item.question_id === questionId &&
              Number(item.given_answer_numeric) === Number(value)
          ) ?? mapped.find(item => item.question_id === questionId);

        if (match) return withCorrectness(match);

        return withCorrectness({
          question_id: Number.isFinite(questionId) ? questionId : undefined,
          question: Number.isFinite(questionId)
            ? `Domanda #${questionId}`
            : key,
          given_answer: value == null ? '—' : String(value),
          given_answer_numeric: value as number | string,
        });
      });

      return {
        attempt: entry.attempt,
        field: entry.field,
        answers,
      };
    });
  }

  if (mapped.length > 0) {
    return [{ attempt: 1, answers: mapped }];
  }

  return fieldEntries.map(entry => ({
    attempt: entry.attempt,
    field: entry.field,
    answers: Object.entries(entry.parsed!).map(([key, value]) => {
      const questionId = Number.parseInt(key.split('-')[1] ?? '', 10);
      return {
        question_id: Number.isFinite(questionId) ? questionId : undefined,
        question: Number.isFinite(questionId)
          ? `Domanda #${questionId}`
          : key,
        given_answer: value == null ? '—' : `Opzione ${String(value)}`,
        given_answer_numeric: value as number | string,
      };
    }),
  }));
}

function AnswerRow({ item }: { item: MappedQuizAnswer }) {
  const correct = Boolean(
    item.is_correct ??
      (item.given_answer_numeric != null &&
        item.correct_answer != null &&
        Number(item.given_answer_numeric) === Number(item.correct_answer))
  );

  const hasCorrectness =
    item.is_correct != null || item.correct_answer != null;

  return (
    <Paper
      withBorder
      p='sm'
      style={{
        background: !hasCorrectness
          ? undefined
          : correct
            ? 'rgba(64, 192, 87, 0.12)'
            : 'rgba(250, 82, 82, 0.12)',
        borderColor: !hasCorrectness
          ? undefined
          : correct
            ? 'rgba(64, 192, 87, 0.45)'
            : 'rgba(250, 82, 82, 0.45)',
      }}
    >
      <Text size='sm' fw={600}>
        {item.question || '—'}
      </Text>
      <Group justify='space-between' mt={6} align='flex-start' wrap='nowrap'>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size='xs' c='dimmed'>
            Risposta data
          </Text>
          <Text size='sm' c={hasCorrectness ? (correct ? 'teal' : 'red') : undefined}>
            {item.given_answer ?? '—'}
          </Text>
          {hasCorrectness && !correct && item.correct_answer_text ? (
            <>
              <Text size='xs' c='dimmed' mt={4}>
                Risposta corretta
              </Text>
              <Text size='sm' c='teal'>
                {item.correct_answer_text}
              </Text>
            </>
          ) : null}
        </Stack>
        {hasCorrectness ? (
          <Badge color={correct ? 'green' : 'red'} variant='filled'>
            {correct ? 'Giusta' : 'Sbagliata'}
          </Badge>
        ) : null}
      </Group>
    </Paper>
  );
}

export const CourseQuizAnswers = memo<CourseQuizAnswersProps>(props => {
  const {
    quizAttempts,
    mappedQuestionsAnswers,
    answer_1,
    answer_2,
    answer_3,
    answer_4,
    answer_5,
  } = props;

  const attempts = useMemo(
    () =>
      resolveAttempts({
        quizAttempts,
        mappedQuestionsAnswers,
        answer_1,
        answer_2,
        answer_3,
        answer_4,
        answer_5,
      }),
    [
      quizAttempts,
      mappedQuestionsAnswers,
      answer_1,
      answer_2,
      answer_3,
      answer_4,
      answer_5,
    ]
  );
  const totalAnswers = attempts.reduce((sum, a) => sum + a.answers.length, 0);
  const defaultOpened = attempts[0] ? [String(attempts[0].attempt)] : [];

  return (
    <Stack gap='sm'>
      <Group justify='space-between'>
        <Title order={5}>Domande e risposte</Title>
        <Badge variant='light' color='gray'>
          {attempts.length} tentativi · {totalAnswers} risposte
        </Badge>
      </Group>

      {attempts.length === 0 ? (
        <Text c='dimmed' size='sm'>
          Nessuna risposta quiz registrata per questo corso.
        </Text>
      ) : (
        <Accordion
          variant='contained'
          radius='md'
          multiple
          defaultValue={defaultOpened}
        >
          {attempts.map(attempt => {
            const correctCount = attempt.answers.filter(a =>
              Boolean(
                a.is_correct ??
                  (a.given_answer_numeric != null &&
                    a.correct_answer != null &&
                    Number(a.given_answer_numeric) === Number(a.correct_answer))
              )
            ).length;

            return (
              <Accordion.Item
                key={`${attempt.field ?? 'attempt'}-${attempt.attempt}`}
                value={String(attempt.attempt)}
              >
                <Accordion.Control>
                  Tentativo {attempt.attempt}
                  {attempt.answers.length
                    ? ` · ${correctCount}/${attempt.answers.length} giuste`
                    : ''}
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap='xs'>
                    {attempt.answers.length === 0 ? (
                      <Text c='dimmed' size='sm'>
                        Nessuna domanda mappata per questo tentativo.
                      </Text>
                    ) : (
                      attempt.answers.map((item, index) => (
                        <AnswerRow
                          key={`${item.question_id ?? item.question}-${index}`}
                          item={item}
                        />
                      ))
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </Stack>
  );
});

CourseQuizAnswers.displayName = 'CourseQuizAnswers';
